import {
  DropdownButtonType,
  List,
  ListItem,
  MultiselectDropdownWidget,
  MultiselectValueSet,
  TextFieldWidget
} from '@tableau/widgets';
import React, { useRef } from 'react';
import { Messages } from '../../../../../gen-src/Localize';
import { DatasourceFieldsByIdQueryVariables, DatasourceFieldsByIdQuery, DatasourceFieldsByIdNodesFragment, FieldSortOrder, DatasourceFieldsByIdDocument, OrderDirection, FieldOrderField } from '../../../../../gen-src/queries';
import {
  BaseTelemetryEvent,
  FeatureFlagsContext,
  GridSortProps,
  PaginatedQueryWrapper,
  SelectionProviderChildProps,
  ValidTelemetryEvent,
  classes,
  OrderBy,
  useTelemetrySender
} from '../../../data-catalog-shared';
import { iconForField } from '../field/functions/FieldFunctions';
import { FieldsValidSortFields } from '../sorting/sortProviders/FieldsGridSortProvider';
import { filterField, searchDataGrid, searchFieldDiv, searchFieldFilter, searchFilterDiv } from '../style/ColumnAndFieldDetailsStyles';
import { DatasourceFieldsDisplayGrid, DatasourceFieldsDisplayGridRowData } from './DatasourceFieldsDisplayGrid';

export type QueryVariables = DatasourceFieldsByIdQueryVariables;
export type QueryResults = DatasourceFieldsByIdQuery;
export type QueryResultListNode = DatasourceFieldsByIdNodesFragment;
export type GridRowData = DatasourceFieldsDisplayGridRowData;

export type DatasourceFieldsQueryGridProps =
  SelectionProviderChildProps &
  GridSortProps<FieldsValidSortFields> & {
    readonly vizportalUrlId: string;
    readonly rowCount: number | null;
    readonly hackyUpdateNumber: number;
  };

export type FieldTextSearchProps = {
  readonly setSearchText: (_: string) => void;
  readonly setRowCount: (_: number) => void;
  readonly resetFilter: React.MutableRefObject<boolean>;
};
  
export function mapResultNode(field: QueryResultListNode, resultData: QueryResults): GridRowData {
  return {
    id: 'id' in field ? field.id : '',
    name: field.name,
    description: field.description,
    descriptionInherited: field.descriptionInherited,
    dataType: 'dataType' in field ? field.dataType : null,
    datasourceId: resultData.publishedDatasources?.[0].vizportalUrlId ?? '',
    fieldIcon: iconForField(field),
    sheetCount: field.downstreamSheetsConnection ? field.downstreamSheetsConnection.totalCount : null,
    labels: field.__typename === 'ColumnField' ? field.columnsConnection?.nodes.flatMap(t => t.labels) : [],
  };
}

export interface CreateLineagePageActionEvent extends ValidTelemetryEvent<BaseTelemetryEvent>{
  type: 'lineage_page_action';
  content_type: string;
  action_type : string;
}

export enum ContentType {
  Datasource = 'datasource',
}

export enum ActionType {
  FieldSearch='field_search',
  ClearSearch='clear_search',
  ResetFilter='reset_filter'
}

export function FieldTextSearch(props: FieldTextSearchProps) {
  const [inProgressSearchText, setInProgressSearchText] = React.useState('');
  const sendFieldSearch = useTelemetrySender<CreateLineagePageActionEvent>({ type: 'lineage_page_action', content_type: ContentType.Datasource, action_type: ActionType.FieldSearch });
  const sendClearFieldSearch = useTelemetrySender<CreateLineagePageActionEvent>({ type: 'lineage_page_action', content_type: ContentType.Datasource, action_type: ActionType.ClearSearch });
  const sendResetFilter = useTelemetrySender<CreateLineagePageActionEvent>({ type: 'lineage_page_action', content_type: ContentType.Datasource, action_type: ActionType.ResetFilter });
  if (props.resetFilter.current) {
    setInProgressSearchText('');
    props.resetFilter.current = false;
    sendResetFilter();
  }
  return (
    <TextFieldWidget
      aria-label={Messages.SearchLabel()}
      kind={'search'}
      label={''}
      onClear={() => { props.setSearchText(''); setInProgressSearchText(''); sendClearFieldSearch();}}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        setInProgressSearchText(e.target.value);
      }}
      onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          props.setSearchText(inProgressSearchText);
          sendFieldSearch();
          props.setRowCount(-1);
        }
      }}
      placeholder={'enter search'}
      testId={'datasource-lineage-field-search'}
      value={inProgressSearchText}
    />
  );
}

function getSortByDisplayText(selectedSort: FieldSortOrder): string {
  if (selectedSort.direction === OrderDirection.ASC) {
    if (selectedSort.field === FieldOrderField.NAME) {
      return Messages.SortByNameAsc();
    } else {
      return Messages.SortByDownstreamSheetsAsc();
    }
  } else {
    if (selectedSort.field === FieldOrderField.NAME) {
      return Messages.SortByNameDesc();
    } else {
      return Messages.SortByDownstreamSheetsDesc();
    }
  }
}

function getMenuList(value: MultiselectValueSet) {
  const sortList: ListItem[] = [{ value: Messages.SortByDropdownSectionHeaderOrder(), group: true }];
  if (value.has(Messages.GridHeaderName())) {
    sortList.push({ value: 'A-Z' }, { value: 'Z-A' });
  } else if (value.has(Messages.Sheets())) {
    sortList.push({ value: Messages.MostToLeast() }, { value: Messages.LeastToMost() });
  }
  sortList.push({ value: Messages.SortByDropdownSectionHeaderOrder(), group: true },
    { value: Messages.GridHeaderName() },
    { value: Messages.Sheets() });
  return sortList;
}

export function DatasourceFieldsQueryGrid(props: DatasourceFieldsQueryGridProps): JSX.Element {
  const {  searchAndFilterOnLineagePageEnabled } = React.useContext(FeatureFlagsContext);
  const resetFilter = useRef(false)
  const [searchText, setSearchText] = React.useState('');
  const [gqlResultCount, setGqlResultCount] = React.useState(props.rowCount);

  const [values, setValues] = React.useState<MultiselectValueSet>(new Set(['A-Z', Messages.GridHeaderName()]));
  const sortList = getMenuList(values);
  const handleFilterChange = (value: string) => {
    const selectedValues = new Set(values);
    switch (value) {
      case 'A-Z':
        selectedValues.delete('Z-A');
        break;
      case 'Z-A':
        selectedValues.delete('A-Z');
        break;
      case Messages.MostToLeast():
        selectedValues.delete(Messages.LeastToMost());
        break;
      case Messages.LeastToMost():
        selectedValues.delete(Messages.MostToLeast());
        break;
      case Messages.GridHeaderName():
        selectedValues.delete(Messages.Sheets());
        break;
      case Messages.Sheets():
        selectedValues.delete(Messages.GridHeaderName());
        selectedValues.add(Messages.MostToLeast())
        break;
    }
    selectedValues.add(value);
    setValues(selectedValues);
  };

  const order = {
    direction: values.has('A-Z') || values.has(Messages.LeastToMost()) ? OrderDirection.ASC : OrderDirection.DESC,
    field: values.has(Messages.GridHeaderName()) ? FieldOrderField.NAME : FieldOrderField.DOWNSTREAM_SHEETS_COUNT
  } as FieldSortOrder;

  return (
    <div className={classes(searchFieldDiv)}>
      <div className={classes(searchFilterDiv)}>
      {searchAndFilterOnLineagePageEnabled && <span className={classes(searchFieldFilter)}>
        {Messages.FieldFilterMessage()}
        <FieldTextSearch
          setRowCount={setGqlResultCount}
          resetFilter={resetFilter}
          setSearchText={setSearchText}
        />
      </span>
      }
      {searchAndFilterOnLineagePageEnabled &&
      <span className={classes(filterField)}>
        
        {Messages.SortByDropdownLabelText()}
        <MultiselectDropdownWidget
          type={DropdownButtonType.Line}
          label=''
          list={<List items={sortList} />}
          value={values}
          onChange={handleFilterChange}
        >
          {getSortByDisplayText(order)}
        </MultiselectDropdownWidget>
      </span>
      }
      </div>
      <div className={classes(searchDataGrid)}>
        <PaginatedQueryWrapper<
          QueryVariables,
          QueryResults,
          QueryResultListNode,
          DatasourceFieldsDisplayGridRowData>
          queryGql={DatasourceFieldsByIdDocument}
          partialQueryVariables={{ vizportalUrlId: props.vizportalUrlId, orderBy: order, searchText: searchText  }}
          selectConnectionListResult={(r) => r && r.publishedDatasources && r.publishedDatasources[0] && r.publishedDatasources[0].fieldsConnection}
          mapResultNode={mapResultNode}
          hackyUpdateNumber={props.hackyUpdateNumber}
          setRowCount={setGqlResultCount}
        >
          {({ rowStore }) => (
            <DatasourceFieldsDisplayGrid
              rowStore={rowStore}
              selectedKeys={props.selectedKeys}
              updateSelectionForKey={props.updateSelectionForKey}
              rowCount={searchText === '' || !searchAndFilterOnLineagePageEnabled ? props.rowCount : gqlResultCount}
              orderBy={order as OrderBy<FieldsValidSortFields>}
              updateSortOrder={props.updateSortOrder}
              resetFilter={() => { setSearchText(''); resetFilter.current = true; }}
            />
          )}
        </PaginatedQueryWrapper>
      </div>
    </div>
  )
}
